import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { SxProps, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { EImageVariant } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import type { ProcessServerConfigFunction } from 'filepond';
import { useCallback, useState } from 'react';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { FaRegImage } from 'react-icons/fa';
import { MdPictureAsPdf } from 'react-icons/md';
import { PiImageBrokenDuotone } from 'react-icons/pi';

import { EMimeType, FILE_FORMATS } from '../../file-uploader/file-uploader';
import { useMediaManagerService } from '../../media-manager/dialog/service';

export type TRhfMediaInlineUploadProps = {
  /** Path of the selected file stored in DB (e.g. /uploads/image.webp) */
  value?: string;
  /** Called with the path of the newly selected file (react-hook-form field.onChange) */
  onChange?: (path: string) => void;
  /** TextField label */
  label?: string;
  /** FilePond server process handler */
  processHandler: ProcessServerConfigFunction;
  /** R2 folder/prefix, default 'uploads' */
  folder?: string;
  /** Accepted MIME types. Defaults to images + PDF. */
  allowedFormats?: EMimeType[];
  /** Which CDN variant URL to display in the input and return via onChange. Defaults to 'original'. */
  selectVariant?: EImageVariant;
  /** Which image size variants to upload. */
  uploadVariants?: EImageVariant[];
  /** Whether the variant checkboxes in the uploader are editable. */
  variantsEditable?: boolean;
  /** MUI TextField error state */
  error?: boolean;
  /** MUI TextField helper text */
  helperText?: string;
  /** Aspect ratio for the crop tool (width / height). e.g. 1 for square. Defaults to free-form. */
  cropAspectRatio?: number;
  /** Number of file thumbnails to show per row in the uploader panel. Defaults to 1. */
  uploaderColumns?: number;
  /** When true, shows system-wide files and uploads are tagged as system assets. Only usable by admin/kit_organizer. */
  isSystemUpload?: boolean;
  /** Preview thumbnail dimensions. Defaults to 180×180. */
  width?: number | string;
  height?: number | string;
  /** When set, preview box uses CSS aspectRatio instead of fixed height. Use with width="100%" for responsive landscape previews. */
  previewAspectRatio?: number;
  sx?: SxProps;
};

export function RhfMediaInlineUpload(props: TRhfMediaInlineUploadProps) {
  const {
    value,
    label,
    onChange,
    processHandler,
    allowedFormats = FILE_FORMATS.IMAGE_AND_PDF,
    selectVariant,
    uploadVariants,
    variantsEditable,
    cropAspectRatio,
    uploaderColumns,
    isSystemUpload,
    width = 180,
    height = 180,
    previewAspectRatio,
    error,
    helperText,
    sx,
  } = props;
  const theme = useTheme();
  const { openMediaManager } = useMediaManagerService();
  const [previewErroredSrc, setPreviewErroredSrc] = useState<string | null>(null);

  const previewSrc = getMediaUrl(value);
  const isPdf = value?.toLowerCase().endsWith('.pdf') ?? false;
  const previewErrored = !!previewSrc && previewErroredSrc === previewSrc;

  const handleOpen = useCallback(() => {
    openMediaManager({
      processHandler,
      allowedFormats,
      selectVariant,
      uploadVariants,
      variantsEditable,
      cropAspectRatio,
      uploaderColumns,
      isSystemUpload,
      onSelect: (_key, variantUrl) => {
        if (variantUrl) {
          onChange?.(variantUrl);
        }
      },
    });
  }, [
    openMediaManager,
    processHandler,
    allowedFormats,
    selectVariant,
    uploadVariants,
    variantsEditable,
    cropAspectRatio,
    uploaderColumns,
    isSystemUpload,
    onChange,
  ]);

  const handlePreviewError = useCallback(() => {
    if (previewSrc) {
      setPreviewErroredSrc(previewSrc);
    }
  }, [previewSrc]);

  const borderColor = error ? theme.palette.error.main : theme.palette.divider;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
      <Typography variant="subtitle2">{label}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box
          sx={{
            position: 'relative',
            height: previewAspectRatio ? 'auto' : height,
            aspectRatio: previewAspectRatio ?? undefined,
            width,
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${borderColor}`,
            bgcolor: theme.palette.action.hover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {value ? (
            isPdf ? (
              <MdPictureAsPdf size={40} color={theme.palette.error.main} />
            ) : previewSrc && !previewErrored ? (
              <Box
                component="img"
                src={previewSrc}
                onError={handlePreviewError}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <PiImageBrokenDuotone size={40} color={theme.palette.action.disabled} />
            )
          ) : (
            <FaRegImage size={36} color={theme.palette.action.disabled} />
          )}
          {/* Sticky bottom Upload / Change button */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AiOutlineCloudUpload />}
              onClick={handleOpen}
              fullWidth
              sx={{
                opacity: 0.5,
                borderRadius: 0,
              }}
            >
              {value ? 'Change' : 'Upload'}
            </Button>
          </Box>
        </Box>
        {helperText && (
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ px: 1.5 }}>
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
